import { useTranslation } from 'react-i18next';

import { CreateUserCompanyMembers } from '@/features/user/components/CreateUser/parts/business/companyMembers/CreateUserCompanyMembers';
import { CreateUserBusinessInformation } from '@/features/user/components/CreateUser/parts/business/CreateUserBusinessInformation';
import { CreateUserBusinessSummary } from '@/features/user/components/CreateUser/parts/business/CreateUserBusinessSummary';
import { CreateUserBusinessVerificationDetails } from '@/features/user/components/CreateUser/parts/business/CreateUserBusinessVerificationDetails';
import { CreateUserPhysicalSummary } from '@/features/user/components/CreateUser/parts/physical/CreateUserPhysicalSummary';
import { CreateUserPhysicalUserInformation } from '@/features/user/components/CreateUser/parts/physical/CreateUserPhysicalUserInformation';
import { CreateUserPhysicalVerificationDetails } from '@/features/user/components/CreateUser/parts/physical/CreateUserPhysicalVerificationDetails';
import { CreateUserSelfSummary } from '@/features/user/components/CreateUser/parts/selfEmployed/CreateUserSelfSummary';
import { CreateUserSelfUserInformation } from '@/features/user/components/CreateUser/parts/selfEmployed/CreateUserSelfUserInformation';
import { CreateUserSelfVerificationDetails } from '@/features/user/components/CreateUser/parts/selfEmployed/CreateUserSelfVerificationDetails';
import { ICreationConfig, IStepConfig } from '@/features/user/store/createUser.types';
import { TUserCreationType } from '@/store/models/dashboard-config.model';

export interface ICreateUserStepsContentsProps {
  steps: Record<TUserCreationType, ICreationConfig<string>>;
}

export function CreateUserStepsContents({ steps }: ICreateUserStepsContentsProps) {
  const { t } = useTranslation();

  const contents: Record<string, IStepConfig> = {
    CreateUserPhysicalUserInformation: {
      name: t('pages.dashboard.createUser.form.pages.userInformation'),
      content: <CreateUserPhysicalUserInformation stepsLength={steps.physical.steps.length} />,
    },
    CreateUserPhysicalVerificationDetails: {
      name: t('pages.dashboard.createUser.form.pages.verificationDetails'),
      content: <CreateUserPhysicalVerificationDetails stepsLength={steps.physical.steps.length} />,
    },
    CreateUserPhysicalSummary: {
      name: t('pages.dashboard.createUser.form.pages.summary'),
      description: t('pages.dashboard.createUser.form.pages.summaryDescription'),
      content: <CreateUserPhysicalSummary stepsLength={steps.physical.steps.length} />,
    },
    CreateUserBusinessInformation: {
      name: t('pages.dashboard.createUser.form.pages.businessInformation'),
      content: <CreateUserBusinessInformation stepsLength={steps.business.steps.length} />,
    },
    CreateUserBusinessVerificationDetails: {
      name: t('pages.dashboard.createUser.form.pages.verificationDetails'),
      content: <CreateUserBusinessVerificationDetails stepsLength={steps.business.steps.length} />,
    },
    CreateUserCompanyMembers: {
      name: t('pages.dashboard.createUser.form.pages.companyMembers'),
      content: <CreateUserCompanyMembers stepsLength={steps.business.steps.length} />,
    },
    CreateUserBusinessSummary: {
      name: t('pages.dashboard.createUser.form.pages.summary'),
      description: t('pages.dashboard.createUser.form.pages.summaryDescription'),
      content: <CreateUserBusinessSummary stepsLength={steps.business.steps.length} />,
    },
    CreateUserSelfUserInformation: {
      name: t('pages.dashboard.createUser.form.pages.userInformation'),
      content: <CreateUserSelfUserInformation stepsLength={steps.selfEmployed.steps.length} />,
    },
    CreateUserSelfVerificationDetails: {
      name: t('pages.dashboard.createUser.form.pages.verificationDetails'),
      content: <CreateUserSelfVerificationDetails stepsLength={steps.selfEmployed.steps.length} />,
    },
    CreateUserSelfSummary: {
      name: t('pages.dashboard.createUser.form.pages.summary'),
      description: t('pages.dashboard.createUser.form.pages.summaryDescription'),
      content: <CreateUserSelfSummary stepsLength={steps.selfEmployed.steps.length} />,
    },
  };

  return Object.keys(steps).reduce((prev, current) => {
    const currentStep = steps[current];
    const currentContents: IStepConfig[] | undefined = currentStep.steps
      ? currentStep.steps.map((key: string) => {
          if (contents[key]) {
            return contents[key];
          } else {
            return { name: '', content: <></> };
          }
        })
      : undefined;
    return {
      ...prev,
      [current]: currentContents ? { ...currentStep, steps: currentContents } : currentStep,
    };
  }, {});
}
